import React from 'react';
import './FormDeleteConfirmModal.css';

interface FormDeleteConfirmModalProps {
  isOpen: boolean;
  formsToDelete: Array<{ id: string; form_name: string; status: string }>;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  getStatusText: (status: string) => string;
}

const FormDeleteConfirmModal: React.FC<FormDeleteConfirmModalProps> = ({
  isOpen,
  formsToDelete,
  isDeleting,
  onConfirm,
  onCancel,
  getStatusText
}) => {
  if (!isOpen) return null;

  const formCount = formsToDelete.length;

  return (
    <div className="form-delete-modal-overlay">
      <div className="form-delete-modal-container">
        {/* Header */}
        <div className="form-delete-modal-header">
          <div className="form-delete-modal-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="#F59E0B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="form-delete-modal-title">
            <h3>Bulk Delete Forms</h3>
            <p className="form-delete-modal-subtitle">
              Are you sure you want to delete {formCount} selected form{formCount > 1 ? 's' : ''}?
            </p>
          </div>
          <button 
            onClick={onCancel}
            className="form-delete-close-btn"
            disabled={isDeleting}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="form-delete-modal-content">
          {/* Warning Section */}
          <div className="form-delete-warning">
            <div className="form-delete-warning-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="#DC2626"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="form-delete-warning-content">
              <p className="form-delete-warning-title">
                This action is irreversible and will permanently delete all form data including:
              </p>
              <ul className="form-delete-warning-list">
                <li>Filled form data</li>
                <li>Auto-filled information</li>
                <li>Manual edits and changes</li>
                <li>Review notes and comments</li>
              </ul>
            </div>
          </div>

          {/* Forms List */}
          <div className="form-delete-list-container">
            <div className="form-delete-list-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z"
                  stroke="#DC2626"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Forms to be deleted:</span>
            </div>
            <div className="form-delete-list">
              {formsToDelete.map(form => (
                <div key={form.id} className="form-delete-item">
                  <div className="form-delete-item-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="form-delete-item-name">{form.form_name}</span>
                  <span className="form-delete-item-status">({getStatusText(form.status)})</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="form-delete-modal-footer">
          <button
            onClick={onCancel}
            className="form-delete-cancel-btn"
            disabled={isDeleting}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="form-delete-confirm-btn"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="spinning">
                  <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Deleting...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path d="M10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Delete {formCount} Form{formCount > 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormDeleteConfirmModal; 